import { createMapInstance, updateMapCenter, updateMinimumZoomLevel } from 'libraries/mapLibrary';
import { store, store$ } from '@redux/store';
import { distinctUntilChanged, filter, first, map, skip } from 'rxjs';
import { LocationType } from "features/searchResultsFacets";
import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import uiStateSlice from 'layout/redux/uiStateSlice';
import { 
  isMapLibraryLoaded,
  isSearchWithinMapEnabled,
  selectIsSearchWithinMapReady,
  selectZoomLevel 
} from '../redux/selectors';
import { locationSearchParameters }
 from '../constants/searchParametersConstants';
import { mapStateSlice } from '../redux/slices';

const { actions } = mapStateSlice;

/* The default settings for the map. */
const defaultMapSettings = {
  clickableIcons: false,
  disableDefaultUI: true,
  disableDoubleClickZoom: false,
  gestureHandling: 'cooperative',
  minZoom: 5,
  maxZoom: 20,
  panControl: false,
  scrollwheel: true,
  zoomControl: true,
  zoomControlStyle: 'SMALL',
};

const rectangularAreaMapSettings = {
  maxZoom: 9,
};

/* The default zoom level for the map according to the business requirements. */
const defaultZoomLevels = {
  city: 11,
  county: 11,
  market: 9,
  zip: 12,
  syntheticGeo: 9,
  rectangularArea: 16,
};

const mapContainer = document.querySelector('#nhs_map');

/**
 * When the map bounds change, update the coordinates in the Redux store.
 *
 * The function is called when the map bounds change. The map bounds are the coordinates of the map's
 * viewport. The map bounds are passed into the function as an argument. The function gets the north
 * east and south west coordinates of the map bounds. The function then dispatches an action to update
 * the coordinates in the Redux store.
 *
 * @param mapBounds - The bounds of the map.
 */
const getBounds = (mapBounds) => {
  const northEast = mapBounds.getNorthEast();
  const southWest = mapBounds.getSouthWest();
  return {
    east: northEast.lng(),
    north: northEast.lat(),
    south: southWest.lat(),
    west: southWest.lng(),
  }
};

/**
 * dispatchBounds:
 * Updates map bounds with east, north, south, and west coordinates.
 * @param {number} east - Eastern boundary coordinate
 * @param {number} north - Northern boundary coordinate
 * @param {number} south - Southern boundary coordinate
 * @param {number} west - Western boundary coordinate
 */
const dispatchBounds = ({east, north, south, west}) => {
  store.dispatch(
    actions.updateCoordinates({
      east,
      north,
      south,
      west,
    })
  );
};

/**
 * expandBounds:
 * When the search within the map is enabled and
 * we are in a zoom in greater than 16, a slight expansion in miles 
 * will be made to prevent the community maps from disappearing 
 * when we move slightly away from their marker.
 *
 * @param {Object} bounds - The bounds of the map.
 * @param {Int} currentZoom - The current map zoom
 */
const expandBounds = (bounds, currentZoom) => {
 const zoomNeighborhood = 16
 const zoomBlock = 17;
 const zoomBuilding = 18;
 const zoomRoom = 19;
 const zoomDetail = 20;
 const roundedZoom = Math.round(currentZoom);

 const zoomMilesMap = {
  [zoomDetail]: 1,
  [zoomRoom]: 1,
  [zoomBuilding]: 1,
  [zoomBlock]: 0.6,
  [zoomNeighborhood]: 0.5,
 };

 const extraMiles = roundedZoom >= zoomNeighborhood ? zoomMilesMap[roundedZoom] : 0;

 const milesToDegreesLat = 0.0145;
 const milesToDegreesLng = 0.0180;

 const newNorthEast = {
  lat: bounds.north + (extraMiles * milesToDegreesLat),
  lng: bounds.east + (extraMiles * milesToDegreesLng),
 };

 const newSouthWest = {
  lat: bounds.south - (extraMiles * milesToDegreesLat),
  lng: bounds.west - (extraMiles * milesToDegreesLng),
 };
 
  return {
    north: newNorthEast.lat,
    south: newSouthWest.lat,
    west: newSouthWest.lng,
    east: newNorthEast.lng,
  }
};

/**
 * When the map is loaded, set the z-index of the map container to 999.
 */
const onMapLoaded = ({zoom}) => {
  mapContainer.style.zIndex = '999';
  store.dispatch(actions.updateMapZoomLevel(zoom))
};

/**
 * Dispatch an action to the store to let it know that the search wihtin map is ready to execute.
 */
const onSearchWithinMapReady = () => {
  store.dispatch(actions.onSearchWithinMapReady({}));
};

/**
 * When the map bounds change, get the bounds of the map and store them in the state.
 * If the search within map is enabled but the search within map is not ready, then dispatch an action
 * to indicate the map coordinates has updated and the search within map is ready to execute.
 *
 * @param mapBounds - The bounds of the map.
 */
const onCoordinatesChanged = (mapBounds) => {
  const state = store.getState();
  const isSearchWithinMapEnable = isSearchWithinMapEnabled(state);
  const isSearchWithinMapReady = selectIsSearchWithinMapReady(state);
  const bounds = getBounds(mapBounds); 
  const currentZoom = selectZoomLevel(state);
  const newBounds = expandBounds(bounds, currentZoom)

  dispatchBounds(newBounds);

  if (isSearchWithinMapEnable && !isSearchWithinMapReady) {
    onSearchWithinMapReady();
  }
};

/* An object that contains the functions that will be added as listeners to the map instance */
const mapEvents = {
  coordinatesChanged: onCoordinatesChanged,
  mapLoaded: onMapLoaded,
};

/**
 * It creates a map instance.
 */
const createMap = () => createMapInstance(mapContainer, defaultMapSettings, mapEvents);

/**
 * If the locationParameters.LocationType is in the defaultZoomLevels object, then set the
 * defaultMapSettings.zoom to the value of the key in the defaultZoomLevels object, otherwise set the
 * defaultMapSettings.zoom to 12.
 *
 * Also assigns the center values for the map using the locationParameters object.
 * @param locationParameters - an object with the information of the current location.
 */
const setMapSettings = (locationParameters) => {
  const zoomLevel = defaultZoomLevels[locationParameters.locationType] ?? 12;
  defaultMapSettings.zoom = zoomLevel;
  defaultMapSettings.center = {
    lat: +locationParameters.originLatitude,
    lng: +locationParameters.originLongitude,
  };
};

/**
 * Update the minimum zoom level for the map according to the rules for the search within map.
 * If the update means the map coordinates are updating, then return without indicating
 * the search within map is ready to execute.
 *
 * @returns Nothing.
 */
const setZoomLevelForRectangularArea = () => {
  const areMapCoordinatesUpdating = updateMinimumZoomLevel(rectangularAreaMapSettings.maxZoom);

  if (areMapCoordinatesUpdating) {
    return;
  }

  onSearchWithinMapReady();
};

/**
 * UpdateMapPosition takes a locationParameters object and returns a newPosition object with the
 * latitude and longitude properties of the locationParameters object.
 * @param locationParameters - an object with the information of the current location.
 */
const updateMapPosition = (locationParameters) => {
  const newPosition = {
    latitude: +locationParameters.originLatitude,
    longitude: +locationParameters.originLongitude,
  };
  updateMapCenter(newPosition);
};

/**
 * A function that is called when the search within map is enabled to update the map center and minimum zoom level
 */
const updateMapInRectangularAreaSearch = () => {
  setZoomLevelForRectangularArea();
};

/**
 * A function that is called when the search within map is disable to update the map center and minimum zoom level.
 */
const updateMapInNotRectangularAreaSearch = (locationParameters) => {
  updateMapPosition(locationParameters);
  updateMinimumZoomLevel(defaultMapSettings.minZoom);
};

/**
 * Subscribing to the store and waiting for the map library to load.
 * The first time the map library is loaded, it will call the createMap function.
 */
store$.pipe(filter(isMapLibraryLoaded), filter(locationSearchParameters), first()).subscribe(createMap);

/**
 * Subscribing to the store.
 * The first time the location search parameters changes, and the location is not a rectangular area,
 * it will set the map settings like center and default zoom.
 * */
store$
  .pipe(filter(locationSearchParameters), map(locationSearchParameters), first())
  .subscribe(setMapSettings);

/**
 * Subscribing to the store.
 * If the current location is not a rectangular area, then the map will update its position when the location changes.
 */
store$
  .pipe(
    filter(isMapLibraryLoaded),
    map(locationSearchParameters),
    distinctUntilChanged(),
    skip(1),
    filter((location) => location.locationType !== LocationType.RectangularArea.toString())
  )
  .subscribe(updateMapInNotRectangularAreaSearch);

/**
 * Subscribing to the store.
 * When the search within map flag is set to true it means the store containing parameters
 * for the location information then it will call the setZoomLevelForRectangularArea function to update
 * the map zoom level according to the search within map rules.
 */
store$
  .pipe(
    filter(isMapLibraryLoaded),
    map(isSearchWithinMapEnabled),
    distinctUntilChanged(),
    filter(Boolean))
  .subscribe(updateMapInRectangularAreaSearch);
  
function zoomChangedHandler(event) {
  const googleAnalyticsDTO = new GoogleAnalyticsEvent('Map Zoom', 'Zoom Changed', event.detail.zoomLevel);      
  store.dispatch(uiStateSlice.actions.trackUserEvent({ googleAnalyticsDTO }));
  store.dispatch(actions.updateMapZoomLevel(event.detail.zoomLevel));
}

mapContainer.addEventListener('zoomChanged', zoomChangedHandler);